import React, { useState } from "react";
import { FaStar, FaCalendarAlt, FaUsers, FaEnvelope, FaChevronDown, FaChevronUp } from "react-icons/fa";

export default function MadAcademyLanding() {
  const coursesData = [
    { id: 1, title: "Full-Stack Web Development", students: 12500, rating: 4.8 },
    { id: 2, title: "Data Science & ML", students: 9800, rating: 4.7 },
    { id: 3, title: "UI/UX Design", students: 6500, rating: 4.6 },
    { id: 4, title: "Cloud & DevOps", students: 7200, rating: 4.5 },
    { id: 5, title: "Cybersecurity Essentials", students: 4300, rating: 4.6 },
  ];

  const demos = [
    { id: 1, title: "Intro to Full-Stack", date: "2025-10-18", time: "10:00 AM IST" },
    { id: 2, title: "Data Science Live Lab", date: "2025-10-25", time: "5:00 PM IST" },
    { id: 3, title: "Design Sprint Workshop", date: "2025-11-02", time: "3:00 PM IST" },
  ];

  const testimonials = [
    { id: 1, name: "Asha R.", role: "Frontend Dev", quote: "MAD Academy helped me transition into a developer role in 6 months!" },
    { id: 2, name: "Ravi K.", role: "Data Analyst", quote: "Hands-on projects and mentors made all the difference." },
    { id: 3, name: "Maya S.", role: "UX Designer", quote: "Practical portfolio work + critique sessions = landed interviews." },
  ];

  const blogs = [
    { id: 1, title: "How to build a portfolio that gets interviews", date: "Oct 1, 2025" },
    { id: 2, title: "5 cloud topics employers actually ask about", date: "Sep 18, 2025" },
    { id: 3, title: "Beginner's guide to version control", date: "Aug 30, 2025" },
  ];

  const faqData = [
    { q: "What is the course duration?", a: "Most courses range from 8 to 24 weeks depending on depth and pace." },
    { q: "Are there any prerequisites?", a: "Basic computer literacy is enough for beginner tracks. Advanced tracks list prerequisites." },
    { q: "Do you provide placement help?", a: "Yes — resume reviews, mock interviews and hiring day events for eligible students." },
  ];

  const popularCourses = [...coursesData].sort((a, b) => b.students - a.students).slice(0, 4);

  const [expandedFaq, setExpandedFaq] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState(null);

  function toggleFaq(i) {
    setExpandedFaq(expandedFaq === i ? null : i);
  }

  function registerDemo(demo) {
    alert(`Registered for: ${demo.title} on ${demo.date} at ${demo.time}`);
  }

  function subscribeNewsletter(e) {
    e.preventDefault();
    const re = /^(([^<>()[\\]\\.,;:\\s@\"]+(\.[^<>()[\\]\\.,;:\\s@\"]+)*)|(\".+\"))@(([^<>()[\\]\\.,;:\\s@\"]+\.)+[^<>()[\\]\\.,;:\\s@\"]{2,})$/i;
    if (!re.test(newsletterEmail)) {
      setNewsletterStatus({ ok: false, msg: "Please enter a valid email." });
      return;
    }
    setNewsletterStatus({ ok: true, msg: "Thanks! Check your inbox to confirm subscription." });
    setNewsletterEmail("");
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <section className="mb-12">
        <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 text-blue-600">Why MAD Academy?</h2>
        <p className="text-gray-600 mb-6">Practical learning, industry mentors, and career-first outcomes.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: "🎯", title: "Project-first curriculum", desc: "Build real projects that showcase your skills to employers." },
            { icon: "🧑‍🏫", title: "Industry mentors", desc: "1:1 mentorship, code reviews and interview prep from working pros." },
            { icon: "💼", title: "Career services", desc: "Resume help, mock interviews and hiring events." },
            { icon: "🌐", title: "Flexible learning", desc: "Self-paced content + live sessions to fit your schedule." }
          ].map((f, i) => (
            <div key={i} className="bg-gradient-to-br from-blue-500 to-red-500 text-white shadow-md rounded-2xl p-6 flex flex-col">
              <div className="text-4xl mb-3">{f.icon}</div>
              <h3 className="font-semibold text-lg">{f.title}</h3>
              <p className="mt-2 text-sm opacity-90">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-red-600">Popular Courses</h2>
          <a className="text-sm font-medium text-blue-600 hover:underline" href="#">View all courses</a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {popularCourses.map(course => (
            <article key={course.id} className="bg-white p-4 rounded-xl border-t-4 border-blue-500 shadow-sm hover:shadow-lg transition">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-blue-700">{course.title}</h3>
                <div className="text-sm text-gray-500">{course.rating} <FaStar className="inline-block ml-1 text-yellow-400" /></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{course.students.toLocaleString()} students</p>
              <div className="mt-4 flex items-center justify-between">
                <button className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700">Enroll</button>
                <button className="text-sm text-blue-600">Preview</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-blue-600">Upcoming Demos</h2>
          <p className="text-sm text-gray-500">Live walkthroughs — seats limited</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {demos.map(d => (
            <div key={d.id} className="bg-gradient-to-br from-red-500 to-blue-500 text-white rounded-xl shadow-md p-5 flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-semibold">{d.title}</h3>
                <div className="text-sm mt-2 flex items-center gap-3 opacity-90">
                  <FaCalendarAlt /> <span>{d.date}</span> • <span>{d.time}</span>
                </div>
              </div>
              <div className="mt-4 flex gap-3">
                <button onClick={() => registerDemo(d)} className="flex-1 px-4 py-2 rounded-lg bg-white text-blue-600 font-medium hover:bg-gray-100">Register</button>
                <button className="px-4 py-2 rounded-lg border border-white text-white hover:bg-white hover:text-red-600">Details</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-4 text-red-600">Testimonials</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map(t => (
            <blockquote key={t.id} className="bg-white p-6 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <p className="text-gray-700">"{t.quote}"</p>
              <footer className="mt-4 text-sm text-gray-500">— {t.name}, <span className="font-medium text-blue-600">{t.role}</span></footer>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-blue-600">Blog Highlights</h2>
          <a className="text-sm text-red-600 hover:underline" href="#">Read all posts</a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map(b => (
            <article key={b.id} className="bg-white rounded-xl border-l-4 border-red-500 shadow p-4">
              <h3 className="font-semibold text-blue-700">{b.title}</h3>
              <p className="text-xs text-gray-500 mt-2">{b.date}</p>
              <div className="mt-4">
                <a className="text-sm text-red-600 hover:underline" href="#">Read post →</a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-bold mb-4 text-red-600">Frequently asked questions</h2>
          <div className="space-y-3">
            {faqData.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border-l-4 border-blue-500 shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full text-left px-4 py-3 flex items-center justify-between focus:outline-none"
                >
                  <div>
                    <div className="font-medium text-blue-700">{f.q}</div>
                    <div className="text-xs text-gray-500">Click to {expandedFaq === i ? "collapse" : "expand"}</div>
                  </div>
                  <div className="ml-4">
                    {expandedFaq === i ? <FaChevronUp className="text-red-600" /> : <FaChevronDown className="text-blue-600" />}
                  </div>
                </button>
                {expandedFaq === i && (
                  <div className="px-4 pb-4 pt-0 text-sm text-gray-600">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-red-500 text-white rounded-2xl p-6 shadow-md">
          <h3 className="text-lg font-semibold mb-2">Join our Newsletter</h3>
          <p className="text-sm mb-4 opacity-90">Get monthly learning tips, new course alerts, and scholarship news.</p>

          <form onSubmit={subscribeNewsletter} className="flex flex-col sm:flex-row gap-3">
            <label htmlFor="email" className="sr-only">Email</label>
            <input
              id="email"
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none"
            />
            <button type="submit" className="px-4 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700">Subscribe</button>
          </form>

          {newsletterStatus && (
            <p className={`mt-3 text-sm ${newsletterStatus.ok ? 'text-green-100' : 'text-yellow-200'}`}>{newsletterStatus.msg}</p>
          )}

          <div className="mt-6 text-xs opacity-80 flex items-center gap-2">
            <FaEnvelope /> <span>We respect your privacy. Unsubscribe anytime.</span>
          </div>
        </div>
      </section>

      <footer className="mt-12 text-center text-sm text-gray-500">© {new Date().getFullYear()} <span className="text-blue-600 font-semibold">MAD Academy</span> — <span className="text-red-600">Learn. Build. Launch.</span></footer>
    </div>
  );
}
